import { useNavigate } from "react-router-dom";

type TileId = "upload" | "standardize";

type TileItem = {
  id: TileId;
  title: string;
  description: string;
  cta: string;
};

const TILE_ITEMS: TileItem[] = [
  {
    id: "upload",
    title: "Upload Standardized Data",
    description:
      "Upload pre-formatted P&L or Census files that already follow the Deloitte template.",
    cta: "Upload Files",
  },
  {
    id: "standardize",
    title: "Standardize Data Flow",
    description:
      "Transform raw employee census data into the Deloitte template format via a guided wizard.",
    cta: "Start Standardization",
  },
];

const ContributeDataTiles = () => {
  const navigate = useNavigate();

  const handleTileAction = (tileId: TileId) => {
    if (tileId === "upload") {
      navigate("/contribute-standardized-data");
      return;
    }

    if (tileId === "standardize") {
      navigate("/contribute-raw-data");
    }
  };

  return (
    <section className="min-h-screen bg-[#f4fbf6] dark:bg-gray-950 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -top-40 -right-32 w-[440px] h-[440px] rounded-full bg-sky-200/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-40 right-1/4 w-[380px] h-[380px] rounded-full bg-indigo-200/20 blur-3xl" />

      <div className="relative z-10 px-4 sm:px-6 py-14 sm:py-16">
        <div className="text-center py-2 sm:py-4">
          <h1 className="text-4xl font-bold text-green-600 dark:text-white mb-3 tracking-tight">
            Contribute Data
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
            Choose how you would like to contribute.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mt-8 sm:mt-10 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TILE_ITEMS.map((tile) => {
              return (
                <button
                  key={tile.id}
                  type="button"
                  onClick={() => handleTileAction(tile.id)}
                  className={[
                    "bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md cursor-pointer flex flex-col h-full text-left",
                    "transition-all duration-200 ease-in-out hover:shadow-xl hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] active:shadow-sm",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600",
                    "border border-gray-200 dark:border-gray-700",
                  ].join(" ")}
                >
                  <h2 className="text-1xl font-bold text-gray-900 dark:text-white mt-0 mb-2 leading-snug">
                    {tile.title}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed max-w-[40ch]">
                    {tile.description}
                  </p>
                  <span className="text-sm text-green-600 dark:text-green-400 leading-relaxed mt-auto pt-4 font-bold text-left inline-flex items-center gap-2">
                    <span>{tile.cta}</span>
                    <svg
                      className="relative top-[1px]"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContributeDataTiles;